import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  QueryKey,
  QueryMeta,
  QueryFunctionContext
} from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

export type FetchQueryParams<TQueryResult, TData, TError = Error> = {
  queryKey: QueryKey;
  url: string;
  requestHeaders?: Record<string, string>;
  options?: Omit<UseQueryOptions<TQueryResult, TError, TData>, 'queryKey' | 'queryFn'>;
  method?: RequestInit['method'];
  meta?: QueryMeta;
  body?: Record<string, any> | null;
};

export type GetQueryParams<TQueryResult, TData, TError = Error> = Omit<
  FetchQueryParams<TQueryResult, TData, TError>,
  'method' | 'body'
>;
export type PostQueryParams<TQueryResult, TData, TError = Error> = Omit<
  FetchQueryParams<TQueryResult, TData, TError>,
  'method'
>;

// returns query options
export function getFetchQuery<TQueryResult, TData = TQueryResult, TError = Error>({
  queryKey,
  url,
  options = {},
  requestHeaders = {},
  method = 'GET',
  body = null
}: FetchQueryParams<TQueryResult, TData, TError>): UseQueryOptions<TQueryResult, TError, TData> {
  const queryFn = async ({ signal }: QueryFunctionContext) => {
    const response = await fetch(url, {
      method,
      headers: {
        'X-REQUEST-ID': uuidv4(),
        'Content-Type': 'application/json',
        ...requestHeaders
      },
      body: method === 'POST' ? JSON.stringify(body) : null,
      signal
    });
    return response.json() as Promise<TQueryResult>;
  };
  return { queryKey, queryFn, ...options };
}

// returns hook to use in component
export function useFetchQuery<TQueryResult, TData = TQueryResult, TError = Error>(
  params: GetQueryParams<TQueryResult, TData, TError>
): UseQueryResult<TData, TError> {
  const queryOptions = getFetchQuery<TQueryResult, TData, TError>({ ...params, method: 'GET' });
  return useQuery<TQueryResult, TError, TData>(queryOptions);
}

export function usePostQuery<TQueryResult, TData = TQueryResult, TError = Error>(
  params: PostQueryParams<TQueryResult, TData, TError>
): UseQueryResult<TData, TError> {
  const queryOptions = getFetchQuery<TQueryResult, TData, TError>({ ...params, method: 'POST' });
  return useQuery<TQueryResult, TError, TData>(queryOptions);
}
